// VenuePage.js

import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { Link, useParams } from "react-router-dom";
import { doc, getDoc, updateDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import { Heading } from "./heading";
import { ImagesField } from "./ImagesField";
import { LocationForm } from "./LocationForm";
import { Switch } from "./switch";
import { useAuth } from "./AuthProvider";
import { esSoloLectura } from "../utils/roles";
import { FormField } from "./FormField";
import { Input } from "./input";
import BilingualDescriptionField from "./BilingualDescriptionField";
import { showAlert } from "../alertService";
import {
  InformationCircleIcon,
  CameraIcon,
  UserCircleIcon,
  Cog6ToothIcon,
  ShoppingBagIcon,
  BuildingOffice2Icon,
  HandRaisedIcon,
  TruckIcon,
  MapPinIcon,
  TableCellsIcon,
  GlobeAmericasIcon,
  CreditCardIcon,
  BuildingOfficeIcon,
  MapIcon,
  DocumentIcon,
  CheckCircleIcon,
  LockClosedIcon,
  TagIcon,
  Squares2X2Icon,
} from "@heroicons/react/20/solid";
import CatalogCheckboxGroup from "./CatalogCheckboxGroup";
import CatalogValueFields from "./CatalogValueFields";
import { loadAllCatalogs } from "../utils/catalogs";
import ArrayObjectEditor from "./ArrayObjectEditor";
import FreeformDocumentsField from "./FreeformDocumentsField";
import EventTypeTagsField from "./EventTypeTagsField";
import SectionShell from "./SectionShell";
import EspaciosManager from "./EspaciosManager";
import { useMultiespacioFlag } from "../hooks/useMultiespacioFlag";
import GaleriasManager from "./GaleriasManager";
import { logBitacora, BITACORA_ACTIONS } from "../utils/bitacora";
import { findScrollParent } from "../utils/scroll";
import { useStickyMaxHeight } from "../hooks/useStickyMaxHeight";
import { ChevronLeftIcon } from "@heroicons/react/20/solid";

const ACCENT = "#1b4d2e";

const SERVICIOS_TABS = [
  {
    id: "serviciosIncluidos",
    label: "Incluidos",
    tooltip:
      "Servicios que ya forman parte del precio de renta del venue. Sin costo adicional para tu evento.",
  },
  {
    id: "serviciosOpcionales",
    label: "Opcionales",
    tooltip:
      "Servicios que el venue ofrece como complemento. Disponibles con un costo adicional, según los necesites.",
  },
  {
    id: "serviciosExclusivos",
    label: "Exclusivos",
    tooltip:
      "Servicios que solo pueden contratarse a través del venue. No se permite traer proveedores externos para estos.",
  },
  {
    id: "serviciosExternos",
    label: "Externos",
    tooltip:
      "Servicios provistos por un tercero, no por el venue. Tú coordinas la contratación directamente con el proveedor.",
  },
];

function parseInstagramUsername(value) {
  if (!value) return "";
  return value
    .replace(/^https?:\/\/(www\.)?instagram\.com\//, "")
    .replace(/\/.*$/, "")
    .replace(/@/g, "");
}

function parseWhatsAppNumber(value) {
  if (!value) return "";
  let num = value.replace(/^https?:\/\/wa\.me\//, "").replace(/\?.*$/, "");
  if (num.includes("/") || num.includes(".")) {
    num = num.replace(/[^\d+]/g, "");
  }
  return num;
}

const hasObjectData = (obj) =>
  obj && typeof obj === "object" && Object.values(obj).some((v) => v != null && v !== "");
const hasArrayData = (arr) => Array.isArray(arr) && arr.length > 0;

// Ancestro que realmente scrollea (el <main> del SidebarLayout, no la
// ventana). null si no hay ninguno antes del body.
const HoverTooltip = ({ content, children }) => {
  const triggerRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  const updatePosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setPos({ top: rect.bottom + 8, left: rect.left + rect.width / 2 });
  }, []);

  const handleEnter = () => {
    updatePosition();
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return undefined;
    const handler = () => updatePosition();
    window.addEventListener("scroll", handler, true);
    window.addEventListener("resize", handler);
    return () => {
      window.removeEventListener("scroll", handler, true);
      window.removeEventListener("resize", handler);
    };
  }, [open, updatePosition]);

  return (
    <>
      <span
        ref={triggerRef}
        onMouseEnter={handleEnter}
        onMouseLeave={() => setOpen(false)}
        onFocus={handleEnter}
        onBlur={() => setOpen(false)}
        onClick={(e) => e.stopPropagation()}
        className="inline-flex items-center"
      >
        {children}
      </span>
      {open &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            role="tooltip"
            style={{ top: pos.top, left: pos.left }}
            className="pointer-events-none fixed z-[9999] w-64 -translate-x-1/2 rounded-lg bg-zinc-900 text-white text-xs font-normal leading-snug px-3 py-2 shadow-lg"
          >
            <span className="absolute bottom-full left-1/2 -translate-x-1/2 -mb-1 border-4 border-transparent border-b-zinc-900" />
            {content}
          </div>,
          document.body,
        )}
    </>
  );
};

const ServiciosSection = ({ catalog, formValues, onChange }) => {
  const [tab, setTab] = useState(SERVICIOS_TABS[0].id);
  return (
    <div>
      <div className="border-b border-zinc-200 mb-4">
        <nav className="-mb-px flex gap-2 overflow-x-auto">
          {SERVICIOS_TABS.map((t) => {
            const count = (formValues[t.id] || []).length;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setTab(t.id)}
                className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap flex items-center gap-1.5 ${
                  active
                    ? "border-[#1b4d2e] text-[#1b4d2e]"
                    : "border-transparent text-zinc-500 hover:text-zinc-700 hover:border-zinc-300"
                }`}
              >
                {t.label}
                {count > 0 && (
                  <span
                    className={`rounded-full px-1.5 text-xs ${
                      active ? "bg-[#1b4d2e]/10 text-[#1b4d2e]" : "bg-zinc-100 text-zinc-500"
                    }`}
                  >
                    {count}
                  </span>
                )}
                <HoverTooltip content={t.tooltip}>
                  <InformationCircleIcon
                    className={`h-3.5 w-3.5 ${
                      active ? "text-[#1b4d2e]/70" : "text-zinc-400"
                    } hover:text-zinc-700`}
                  />
                </HoverTooltip>
              </button>
            );
          })}
        </nav>
      </div>
      <CatalogCheckboxGroup
        catalog={catalog}
        selectedIds={formValues[tab] || []}
        onChange={(ids) => onChange(tab, ids)}
        label={`Selecciona los servicios ${SERVICIOS_TABS.find((t) => t.id === tab).label.toLowerCase()}`}
      />
    </div>
  );
};

const SkeletonBar = ({ className = "" }) => (
  <div className={`rounded bg-zinc-200/70 ${className}`} />
);

const SectionCardSkeleton = () => (
  <div className="rounded-xl border border-zinc-200 bg-white shadow-sm p-4">
    <div className="flex items-center gap-3">
      <div className="h-8 w-8 rounded-lg bg-zinc-200/70" />
      <SkeletonBar className="h-4 w-40" />
    </div>
    <div className="mt-4 space-y-2">
      <SkeletonBar className="h-3 w-full" />
      <SkeletonBar className="h-3 w-5/6" />
      <SkeletonBar className="h-3 w-2/3" />
    </div>
  </div>
);

const VenuePageSkeleton = () => (
  <div className="container mx-auto p-4 pb-28 animate-pulse max-lg:px-0" aria-busy="true">
    {/* Header card */}
    <div className="rounded-2xl border border-zinc-200 bg-white shadow-sm overflow-hidden mb-6">
      <div className="flex flex-col sm:flex-row">
        <div className="sm:w-48 sm:shrink-0 h-32 sm:h-auto bg-zinc-200/70" />
        <div className="flex-1 p-5 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0 space-y-3 flex-1">
            <SkeletonBar className="h-3 w-24" />
            <SkeletonBar className="h-6 w-2/3" />
            <SkeletonBar className="h-3 w-1/2" />
            <SkeletonBar className="h-2 w-40 mt-3" />
          </div>
          <div className="flex flex-col gap-2 shrink-0">
            <SkeletonBar className="h-6 w-32 rounded-full" />
            <SkeletonBar className="h-6 w-32 rounded-full" />
          </div>
        </div>
      </div>
    </div>

    <div className="flex gap-8">
      {/* Sidebar */}
      <aside className="hidden lg:block w-56 shrink-0">
        <div className="sticky top-6 space-y-5">
          {[0, 1, 2].map((g) => (
            <div key={g}>
              <SkeletonBar className="h-3 w-16 mb-3" />
              <div className="space-y-1.5">
                {[0, 1, 2].map((i) => (
                  <SkeletonBar key={i} className="h-7 w-full" />
                ))}
              </div>
            </div>
          ))}
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0 space-y-4">
        {[0, 1, 2, 3].map((i) => (
          <SectionCardSkeleton key={i} />
        ))}
      </main>
    </div>
  </div>
);

// `venueId`/`embebido`: la ficha se usa como ruta propia (/dashboard/venues/:id,
// que es como la abren los admins desde su lista) y también incrustada en la
// pestaña Venue de Configuración, que es por donde entran los owners. Lo
// único que cambia es de dónde sale el id y el cromo de página.
const VenuePage = ({ venueId = null, embebido = false }) => {
  const params = useParams();
  const id = venueId || params.id;
  const multiespacioActivo = useMultiespacioFlag(id);
  const [formValues, setFormValues] = useState(null);
  const [initialSnapshot, setInitialSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const { userInfo, user } = useAuth();
  // Modo lectura de la ficha: nació para el viewer (commit "Usuarios
  // viewers") pero la ruta nunca lo dejó entrar; hoy lo ejerce el
  // inversionista (P-FRA-13).
  const soloLectura = esSoloLectura(userInfo);
  const isAdmin = userInfo?.role === "admin";
  const [catalogs, setCatalogs] = useState(null);
  const [activeSection, setActiveSection] = useState(null);
  const mainRef = useRef(null);

  useEffect(() => {
    const fetchVenue = async () => {
      if (!id) {
        setError("No venue ID provided");
        setLoading(false);
        return;
      }
      try {
        const docRef = doc(db, "venues", id);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          setFormValues(data);
          setInitialSnapshot(JSON.stringify(data));
        } else {
          setError("Venue not found.");
        }
      } catch (err) {
        console.error("Error fetching venue:", err);
        setError("Error fetching venue.");
      } finally {
        setLoading(false);
      }
    };
    fetchVenue();
  }, [id]);

  useEffect(() => {
    async function fetchCatalogs() {
      try {
        const loaded = await loadAllCatalogs();
        setCatalogs(loaded);
      } catch (e) {
        console.error("Error fetching catalogs:", e);
        showAlert("Error al cargar los catalogos, comuníquese con el administrador.", "error");
      }
    }
    fetchCatalogs();
  }, []);

  const isDirty = useMemo(() => {
    if (!formValues || !initialSnapshot) return false;
    return JSON.stringify(formValues) !== initialSnapshot;
  }, [formValues, initialSnapshot]);

  useEffect(() => {
    if (!isDirty) return undefined;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isDirty]);

  const handleChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    setFormValues((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  }, []);

  const handleNestedChange = useCallback((group, name, value) => {
    setFormValues((prev) => ({
      ...prev,
      [group]: { ...prev[group], [name]: value },
    }));
  }, []);

  const handleReorderImages = useCallback((newImages) => {
    setFormValues((prev) => {
      if (!prev || !prev.images) return prev;
      return { ...prev, images: [...newImages] };
    });
  }, []);

  const handleCatalogIdsChange = useCallback((field, ids) => {
    setFormValues((prev) => ({ ...prev, [field]: ids }));
  }, []);

  const handleCatalogValueChange = useCallback((field, key, value) => {
    setFormValues((prev) => ({
      ...prev,
      [field]: { ...prev[field], [key]: value },
    }));
  }, []);

  // EspaciosManager escribe el rollup directo en el doc del venue. Este patch
  // se aplica a formValues Y al snapshot inicial: si solo tocara formValues,
  // el save bar marcaría dirty; si no tocara ninguno, un "Guardar cambios"
  // posterior pisaría el rollup recién escrito con la copia vieja.
  const applyVenuePatch = useCallback(({ fields = {}, basicInfo3 }) => {
    const merge = (base) => {
      const next = { ...base, ...fields };
      if (basicInfo3 !== undefined && !Array.isArray(next.basicInfo)) {
        next.basicInfo = { ...(next.basicInfo || {}), 3: basicInfo3 };
      }
      return next;
    };
    setFormValues((prev) => (prev ? merge(prev) : prev));
    setInitialSnapshot((prev) => (prev ? JSON.stringify(merge(JSON.parse(prev))) : prev));
  }, []);

  const buildSocialUrls = (social) => {
    const result = { ...social };
    if (result.instagram) {
      const username = parseInstagramUsername(result.instagram);
      result.instagram = username ? `https://www.instagram.com/${username}/` : "";
    }
    if (result.whatsapp) {
      const phone = parseWhatsAppNumber(result.whatsapp);
      if (phone && /^\+?\d+$/.test(phone)) {
        const msg = encodeURIComponent("¡Hola! Vi su página en Venueboard y");
        result.whatsapp = `https://wa.me/${phone}?text=${msg}`;
      } else {
        result.whatsapp = "";
      }
    }
    return result;
  };

  const handleSubmit = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    setSaving(true);
    try {
      const docRef = doc(db, "venues", id);
      const dataToSave = {
        ...formValues,
        social: buildSocialUrls(formValues.social || {}),
        lastUpdate: serverTimestamp(),
      };
      // formValues es el doc completo del load, así que este save arrastraría
      // las llaves denormalizadas que mantienen OTROS flujos: `multiespacio`
      // (toggle admin — las rules rechazan el update ENTERO de un owner cuyo
      // diff la toque, p.ej. con una copia vieja tras un cambio del admin) y
      // el rollup/contadores de los CRUD de espacios y galerías (pisarlos con
      // copia vieja desincroniza calendario y disponibilidad pública). Se
      // quitan siempre: nada del formulario los edita.
      [
        "multiespacio",
        "espaciosResumen",
        "capacidadMin",
        "capacidadMax",
        "espaciosCount",
        "permiteEventosSimultaneos",
        "galeriasPublicadasCount",
      ].forEach((k) => delete dataToSave[k]);
      await updateDoc(docRef, dataToSave);
      setInitialSnapshot(JSON.stringify(formValues));
      showAlert("Venue actualizado correctamente!", "success");
      logBitacora(
        id,
        {
          action: BITACORA_ACTIONS.VENUE_EDIT,
          entity: "venue",
          entityId: id,
          entityName: formValues.name || "",
          details: {},
        },
        userInfo,
        user,
      );
    } catch (err) {
      console.error("Error actualizando el venue:", err);
      showAlert("Error al actualizar el venue.", "error");
    } finally {
      setSaving(false);
    }
  };

  const discardChanges = () => {
    if (!initialSnapshot) return;
    if (!window.confirm("¿Descartar los cambios sin guardar?")) return;
    setFormValues(JSON.parse(initialSnapshot));
  };

  // Build section catalog (after formValues are loaded)
  const groups = useMemo(() => {
    if (!formValues || !catalogs) return [];
    return [
      {
        id: "identidad",
        label: "Identidad",
        sections: [
          {
            id: "venue",
            title: "Datos del venue",
            icon: <UserCircleIcon className="w-5 h-5" />,
            hasData: !!(formValues.name && formValues.address && formValues.description),
            render: () => (
              <div className="space-y-4">
                <FormField
                  label="Nombre"
                  name="name"
                  value={formValues.name}
                  onChange={handleChange}
                />
                <FormField
                  label="Dirección"
                  name="address"
                  value={formValues.address}
                  onChange={handleChange}
                />
                <BilingualDescriptionField
                  valueEs={formValues.description || ""}
                  valueEn={formValues.descriptionEn || ""}
                  onChangeEs={(v) =>
                    setFormValues((prev) => ({ ...prev, description: v }))
                  }
                  onChangeEn={(v) =>
                    setFormValues((prev) => ({ ...prev, descriptionEn: v }))
                  }
                  rows={6}
                />
              </div>
            ),
          },
          {
            id: "imagenes",
            title: "Imágenes",
            icon: <CameraIcon className="w-5 h-5" />,
            hasData: hasArrayData(formValues.images),
            render: () => (
              <ImagesField
                label="Images"
                images={formValues.images}
                onReorderImages={handleReorderImages}
                venueId={id}
              />
            ),
          },
          {
            id: "informacion",
            title: "Información básica",
            icon: <InformationCircleIcon className="w-5 h-5" />,
            hasData: hasObjectData(formValues.basicInfo),
            render: () => (
              <CatalogValueFields
                catalog={catalogs.basicInfo || []}
                values={formValues.basicInfo || {}}
                onChange={(key, value) => handleCatalogValueChange("basicInfo", key, value)}
                label="Información básica del venue"
              />
            ),
          },
          {
            id: "redes",
            title: "Redes Sociales",
            icon: <GlobeAmericasIcon className="w-5 h-5" />,
            hasData: !!(
              formValues.social?.instagram ||
              formValues.social?.whatsapp ||
              formValues.social?.website
            ),
            render: () => (
              <div className="space-y-4">
                <div className="mb-4">
                  <label className="block mb-1 font-medium">Instagram</label>
                  <div className="flex items-center gap-2">
                    <span className="shrink-0 rounded-lg bg-zinc-100 px-2.5 py-1.5 text-sm font-medium text-zinc-500 dark:bg-zinc-800 dark:text-zinc-400">
                      @
                    </span>
                    <Input
                      value={parseInstagramUsername(formValues.social?.instagram || "")}
                      onChange={(e) => {
                        const username = e.target.value.replace(/@/g, "").replace(/\s/g, "");
                        setFormValues((prev) => ({
                          ...prev,
                          social: { ...prev.social, instagram: username },
                        }));
                      }}
                      placeholder="tu.usuario"
                    />
                  </div>
                </div>
                <div className="mb-4">
                  <label className="block mb-1 font-medium">WhatsApp</label>
                  <Input
                    type="tel"
                    value={parseWhatsAppNumber(formValues.social?.whatsapp || "")}
                    onChange={(e) => {
                      const phone = e.target.value.replace(/[^\d+]/g, "");
                      setFormValues((prev) => ({
                        ...prev,
                        social: { ...prev.social, whatsapp: phone },
                      }));
                    }}
                    placeholder="525523090790"
                  />
                  <p className="mt-1.5 text-xs text-zinc-400 dark:text-zinc-500">
                    Solo el número con código de país.
                  </p>
                </div>
                <FormField
                  label="Web"
                  name="website"
                  value={formValues.social?.website || ""}
                  onChange={(e) => {
                    const value = e.target.value;
                    setFormValues((prev) => ({
                      ...prev,
                      social: { ...prev.social, website: value },
                    }));
                  }}
                  placeholder="https://venueboard.com"
                />
              </div>
            ),
          },
        ],
      },
      {
        id: "espacio",
        label: "Espacio",
        sections: [
          {
            id: "instalaciones",
            title: "Instalaciones",
            icon: <BuildingOffice2Icon className="w-5 h-5" />,
            hasData: hasArrayData(formValues.instalaciones),
            render: () => (
              <CatalogCheckboxGroup
                catalog={catalogs.instalaciones || []}
                selectedIds={formValues.instalaciones || []}
                onChange={(ids) => handleCatalogIdsChange("instalaciones", ids)}
                label="Selecciona las instalaciones"
              />
            ),
          },
          {
            id: "alimentos",
            title: "Alimentos y Bebidas",
            icon: <ShoppingBagIcon className="w-5 h-5" />,
            hasData: hasArrayData(formValues.alimentosBebidas),
            render: () => (
              <CatalogCheckboxGroup
                catalog={catalogs.alimentosBebidas || []}
                selectedIds={formValues.alimentosBebidas || []}
                onChange={(ids) => handleCatalogIdsChange("alimentosBebidas", ids)}
                label="Selecciona alimentos y bebidas"
              />
            ),
          },
          {
            id: "areas",
            title: "Áreas Adicionales",
            icon: <HandRaisedIcon className="w-5 h-5" />,
            hasData: hasArrayData(formValues.areasAdicionales),
            render: () => (
              <CatalogCheckboxGroup
                catalog={catalogs.areasAdicionales || []}
                selectedIds={formValues.areasAdicionales || []}
                onChange={(ids) => handleCatalogIdsChange("areasAdicionales", ids)}
                label="Selecciona áreas adicionales"
              />
            ),
          },
          {
            id: "aforo",
            title: "Aforo",
            icon: <TableCellsIcon className="w-5 h-5" />,
            hasData: hasObjectData(formValues.capacityFields),
            render: () => (
              <CatalogValueFields
                catalog={catalogs.capacityFields || []}
                values={formValues.capacityFields || {}}
                onChange={(key, value) => handleCatalogValueChange("capacityFields", key, value)}
                label="Capacidad por tipo (Personas)"
              />
            ),
          },
          {
            id: "espacios",
            title: "Espacios y salones",
            icon: <Squares2X2Icon className="w-5 h-5" />,
            hasData: hasArrayData(formValues.espaciosResumen),
            render: () => (
              <EspaciosManager
                venueId={id}
                basicInfo={formValues.basicInfo}
                permiteEventosSimultaneos={formValues.permiteEventosSimultaneos !== false}
                onVenuePatch={applyVenuePatch}
                catalogs={catalogs}
                readOnly={soloLectura}
                multiespacioActivo={multiespacioActivo}
              />
            ),
          },
          {
            id: "galerias",
            title: "Galerías de eventos",
            icon: <CameraIcon className="w-5 h-5" />,
            // Completo = al menos una galería PUBLICADA (borradores no
            // cuentan). Usa el contador denormalizado que GaleriasManager
            // mantiene en el doc del venue para el sitemap.
            hasData: (formValues.galeriasPublicadasCount || 0) > 0,
            render: () => (
              <GaleriasManager
                venueId={id}
                espacios={formValues.espaciosResumen || []}
                onVenuePatch={applyVenuePatch}
                readOnly={soloLectura}
              />
            ),
          },
        ],
      },
      {
        id: "servicios",
        label: "Servicios",
        sections: [
          {
            id: "servicios",
            title: "Servicios",
            icon: <Cog6ToothIcon className="w-5 h-5" />,
            hasData:
              hasArrayData(formValues.serviciosIncluidos) ||
              hasArrayData(formValues.serviciosOpcionales) ||
              hasArrayData(formValues.serviciosExclusivos) ||
              hasArrayData(formValues.serviciosExternos),
            render: () => (
              <ServiciosSection
                catalog={catalogs.servicios || []}
                formValues={formValues}
                onChange={handleCatalogIdsChange}
              />
            ),
          },
        ],
      },
      {
        id: "logistica",
        label: "Logística",
        sections: [
          {
            id: "transporte",
            title: "Transporte Terrestre",
            icon: <MapIcon className="w-5 h-5" />,
            hasData: hasArrayData(formValues.transporteTerrestre),
            render: () => (
              <CatalogCheckboxGroup
                catalog={catalogs.transporteTerrestre || []}
                selectedIds={formValues.transporteTerrestre || []}
                onChange={(ids) => handleCatalogIdsChange("transporteTerrestre", ids)}
                label="Selecciona opciones de transporte terrestre"
              />
            ),
          },
          {
            id: "estacionamiento",
            title: "Estacionamiento",
            icon: <TruckIcon className="w-5 h-5" />,
            hasData: hasObjectData(formValues.estacionamiento),
            render: () => (
              <CatalogValueFields
                catalog={catalogs.estacionamiento || []}
                values={formValues.estacionamiento || {}}
                onChange={(key, value) => handleCatalogValueChange("estacionamiento", key, value)}
                label="Información de estacionamiento"
              />
            ),
          },
          {
            id: "pagos",
            title: "Pagos",
            icon: <CreditCardIcon className="w-5 h-5" />,
            hasData: hasObjectData(formValues.politicaPagos),
            render: () => (
              <CatalogValueFields
                catalog={catalogs.politicaPagos || []}
                values={formValues.politicaPagos || {}}
                onChange={(key, value) => handleCatalogValueChange("politicaPagos", key, value)}
                label="Política de pagos"
              />
            ),
          },
          {
            id: "documentos",
            title: "Documentos",
            icon: <DocumentIcon className="w-5 h-5" />,
            hasData: hasArrayData(formValues.documentos) || hasObjectData(formValues.documentos),
            render: () => (
              <FreeformDocumentsField
                catalog={catalogs.documentos || []}
                values={formValues.documentos}
                onChange={(arr) => setFormValues((prev) => ({ ...prev, documentos: arr }))}
                venueId={id}
                currentUser={user}
              />
            ),
          },
        ],
      },
      ...(isAdmin
        ? [
            {
              id: "admin",
              label: "Admin",
              adminOnly: true,
              sections: [
                {
                  id: "tipos-evento",
                  title: "Tipos de Evento",
                  icon: <TagIcon className="w-5 h-5" />,
                  hasData: hasArrayData(formValues.eventTypes),
                  render: () => (
                    <EventTypeTagsField
                      value={formValues.eventTypes || []}
                      onChange={(arr) =>
                        setFormValues((prev) => ({ ...prev, eventTypes: arr }))
                      }
                    />
                  ),
                },
                {
                  id: "aeropuertos",
                  title: "Aeropuertos",
                  icon: <GlobeAmericasIcon className="w-5 h-5" />,
                  hasData: hasArrayData(formValues.aeropuertos),
                  render: () => (
                    <ArrayObjectEditor
                      value={formValues.aeropuertos || []}
                      onChange={(arr) =>
                        setFormValues((prev) => ({ ...prev, aeropuertos: arr }))
                      }
                      fields={[
                        { key: "name", label: "Nombre", type: "text" },
                        { key: "distance", label: "Distancia", type: "text" },
                        { key: "travelTime", label: "Tiempo de viaje", type: "text" },
                      ]}
                      label="Aeropuertos cercanos"
                    />
                  ),
                },
                {
                  id: "hoteles",
                  title: "Hoteles",
                  icon: <BuildingOfficeIcon className="w-5 h-5" />,
                  hasData: hasArrayData(formValues.hoteles),
                  render: () => (
                    <ArrayObjectEditor
                      value={formValues.hoteles || []}
                      onChange={(arr) =>
                        setFormValues((prev) => ({ ...prev, hoteles: arr }))
                      }
                      fields={[
                        { key: "name", label: "Nombre", type: "text" },
                        { key: "distance", label: "Distancia", type: "text" },
                        { key: "travelTime", label: "Tiempo de viaje", type: "text" },
                        { key: "url", label: "URL", type: "url" },
                      ]}
                      label="Hoteles cercanos"
                    />
                  ),
                },
                {
                  id: "localizacion",
                  title: "Localización",
                  icon: <MapPinIcon className="w-5 h-5" />,
                  hasData: hasObjectData(formValues.location),
                  render: () => (
                    <LocationForm
                      location={formValues.location}
                      onChange={(name, value) =>
                        handleNestedChange("location", name, value)
                      }
                      venueId={id}
                    />
                  ),
                },
                {
                  id: "recorrido",
                  title: "Recorrido",
                  icon: <MapIcon className="w-5 h-5" />,
                  hasData: !!(formValues.recorrido?.tipo && formValues.recorrido?.id),
                  render: () => (
                    <div className="space-y-4">
                      <FormField
                        label="Tipo de Recorrido"
                        name="recorridoTipo"
                        value={formValues.recorrido?.tipo || ""}
                        onChange={(e) => {
                          const value = e.target.value;
                          setFormValues((prev) => ({
                            ...prev,
                            recorrido: { ...prev.recorrido, tipo: value },
                          }));
                        }}
                        placeholder="Ejemplo: matterport"
                      />
                      <FormField
                        label="ID de Recorrido"
                        name="recorridoId"
                        value={formValues.recorrido?.id || ""}
                        onChange={(e) => {
                          const value = e.target.value;
                          setFormValues((prev) => ({
                            ...prev,
                            recorrido: { ...prev.recorrido, id: value },
                          }));
                        }}
                        placeholder="Ejemplo: ZemqbJEApKb"
                      />
                    </div>
                  ),
                },
              ],
            },
          ]
        : []),
    ];
  }, [
    formValues,
    catalogs,
    isAdmin,
    soloLectura,
    id,
    multiespacioActivo,
    user,
    handleChange,
    handleCatalogIdsChange,
    handleCatalogValueChange,
    handleNestedChange,
    handleReorderImages,
    applyVenuePatch,
  ]);

  const allSectionIds = useMemo(
    () => groups.flatMap((g) => g.sections.map((s) => s.id)),
    [groups],
  );

  const tocRef = useRef(null);

  // Alto del índice lateral, medido contra su contenedor real (el `top: 16`
  // debe coincidir con el `top-4` del sticky). Ver useStickyMaxHeight.
  useStickyMaxHeight(tocRef, { top: 16, deps: [groups.length] });

  // El índice scrollea aparte del contenido, así que hay que arrastrarlo a
  // la sección activa: si no, al bajar por el formulario las últimas
  // secciones nunca aparecen en la barra. `nearest` no mueve nada cuando el
  // item ya está a la vista, así que no hay saltos.
  // OJO: nada de scrollIntoView aquí — en Chrome/Safari cualquier
  // scrollIntoView cancela el scroll suave en curso del <main>, y este efecto
  // dispara en cuanto el scrollspy cambia de sección a media animación. Se
  // replica `nearest` a mano tocando SOLO el scrollTop del índice.
  useEffect(() => {
    if (!activeSection || !tocRef.current) return;
    const nav = tocRef.current;
    const item = nav.querySelector(`[data-section="${activeSection}"]`);
    if (!item) return;
    const navRect = nav.getBoundingClientRect();
    const itemRect = item.getBoundingClientRect();
    if (itemRect.top < navRect.top) {
      nav.scrollTop += itemRect.top - navRect.top;
    } else if (itemRect.bottom > navRect.bottom) {
      nav.scrollTop += itemRect.bottom - navRect.bottom;
    }
  }, [activeSection]);

  // Scrollspy via IntersectionObserver
  useEffect(() => {
    if (allSectionIds.length === 0) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible.length > 0) {
          setActiveSection(visible[0].target.id);
        }
      },
      { rootMargin: "-15% 0px -70% 0px", threshold: 0 },
    );
    allSectionIds.forEach((sectionId) => {
      const el = document.getElementById(sectionId);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [allSectionIds]);

  // Progress
  const progress = useMemo(() => {
    const all = groups.flatMap((g) => g.sections);
    const filled = all.filter((s) => s.hasData).length;
    return { filled, total: all.length };
  }, [groups]);

  if (loading || !catalogs) return <VenuePageSkeleton />;
  if (error) return <div className="p-4 text-center text-red-600">{error}</div>;
  if (!formValues) return null;

  const cover =
    formValues.images && formValues.images.length ? formValues.images[0] : null;

  // Show "Volver a venues" only when the user actually has a list to go back to.
  // Single-venue owners/viewers are auto-redirected by VenuesList, so the link would just bounce.
  // Dentro de Configuración no hay a dónde "volver": la lista de venues es
  // de admins y el owner ya está en su pantalla.
  const showBackLink =
    !embebido && (isAdmin || (userInfo?.venues?.length || 0) > 1);

  const scrollToSection = (sectionId) => {
    const el = document.getElementById(sectionId);
    if (!el) return;
    setActiveSection(sectionId);
    const scroller = findScrollParent(el);
    if (scroller) {
      const top =
        el.getBoundingClientRect().top -
        scroller.getBoundingClientRect().top +
        scroller.scrollTop -
        16;
      scroller.scrollTo({ top: Math.max(top, 0), behavior: "smooth" });
    } else {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <div
      className={
        embebido ? "pb-28" : "container mx-auto p-4 pb-28 max-lg:px-0"
      }
    >
      {showBackLink && (
        <Link
          to="/dashboard/venues"
          className="inline-flex items-center gap-1 text-sm text-zinc-500 hover:text-zinc-800 mb-3"
        >
          <ChevronLeftIcon className="h-4 w-4" />
          Volver a venues
        </Link>
      )}
      {/* Header card */}
      <div className="rounded-2xl border border-zinc-200 bg-white shadow-sm overflow-hidden mb-6">
        <div className="flex flex-col sm:flex-row">
          <div className="sm:w-48 sm:shrink-0 h-32 sm:h-auto bg-zinc-100 relative">
            {cover ? (
              <img src={cover} alt="" className="absolute inset-0 h-full w-full object-cover" />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center">
                <BuildingOffice2Icon className="h-10 w-10 text-zinc-300" />
              </div>
            )}
          </div>
          <div className="flex-1 p-5 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0">
              <div className="text-xs uppercase tracking-wide text-zinc-400">
                {soloLectura ? "Venue" : "Editando venue"}
              </div>
              <Heading level={2} className="truncate">
                {formValues.name}
              </Heading>
              {formValues.address && (
                <div className="text-sm text-zinc-500 mt-0.5 truncate">{formValues.address}</div>
              )}
              <div className="mt-2 text-xs text-zinc-500">
                {progress.filled} de {progress.total} secciones completas
              </div>
              <div className="mt-1 h-1.5 w-40 max-w-full bg-zinc-100 rounded-full overflow-hidden">
                <div
                  className="h-full transition-all"
                  style={{
                    width: `${progress.total ? (progress.filled / progress.total) * 100 : 0}%`,
                    backgroundColor: ACCENT,
                  }}
                />
              </div>
            </div>
            <div className="flex flex-col sm:items-end gap-2 shrink-0">
              {soloLectura ? (
                <span
                  className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${
                    formValues.isActive
                      ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200"
                      : "bg-zinc-100 text-zinc-600 ring-1 ring-zinc-200"
                  }`}
                >
                  <span
                    className={`h-1.5 w-1.5 rounded-full ${
                      formValues.isActive ? "bg-emerald-500" : "bg-zinc-400"
                    }`}
                  />
                  {formValues.isActive ? "Activo" : "Inactivo"}
                </span>
              ) : (
                <>
                  <label
                    htmlFor="toggle-active"
                    className="flex items-center gap-3 text-sm text-zinc-700"
                  >
                    <span className="w-32 sm:w-auto sm:text-right">
                      {isAdmin ? "Público" : "Status"}
                    </span>
                    <span
                      className={`min-w-[60px] text-center text-xs font-medium px-2 py-0.5 rounded-full ${
                        formValues.isActive
                          ? "bg-emerald-50 text-emerald-700"
                          : "bg-zinc-100 text-zinc-500"
                      }`}
                    >
                      {formValues.isActive ? "Activo" : "Inactivo"}
                    </span>
                    <Switch
                      color="accent"
                      id="toggle-active"
                      checked={formValues.isActive}
                      onChange={(checked) =>
                        handleChange({ target: { name: "isActive", value: checked } })
                      }
                    />
                  </label>
                  {isAdmin && (
                    <label
                      htmlFor="toggle-active-admin"
                      className="flex items-center gap-3 text-sm text-zinc-700"
                    >
                      <span className="w-32 sm:w-auto sm:text-right">Admin</span>
                      <span
                        className={`min-w-[60px] text-center text-xs font-medium px-2 py-0.5 rounded-full ${
                          formValues.isActiveAdmin
                            ? "bg-emerald-50 text-emerald-700"
                            : "bg-zinc-100 text-zinc-500"
                        }`}
                      >
                        {formValues.isActiveAdmin ? "Activo" : "Inactivo"}
                      </span>
                      <Switch
                        color="accent"
                        id="toggle-active-admin"
                        checked={formValues.isActiveAdmin}
                        onChange={(checked) =>
                          handleChange({
                            target: { name: "isActiveAdmin", value: checked },
                          })
                        }
                      />
                    </label>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="flex gap-8">
        {/* Sidebar TOC */}
        <aside className="hidden lg:block w-56 shrink-0">
          {/* Scroll propio. El max-height lo pone el efecto de arriba
              midiendo el contenedor que realmente scrollea; el de aquí es
              solo el valor inicial antes del primer render. Scrollbar oculto
              (mismo criterio que el resto del proyecto) y el índice se
              arrastra solo a la sección activa. */}
          <nav
            ref={tocRef}
            className="sticky top-4 overflow-y-auto overscroll-contain pr-1 space-y-5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            {groups.map((group) => (
              <div key={group.id}>
                <div className="text-[11px] font-semibold uppercase tracking-wider text-zinc-400 mb-2">
                  {group.label}
                </div>
                <ul className="space-y-0.5">
                  {group.sections.map((section) => {
                    const active = activeSection === section.id;
                    return (
                      <li key={section.id}>
                        <button
                          type="button"
                          data-section={section.id}
                          onClick={() => scrollToSection(section.id)}
                          className={`w-full text-left flex items-center gap-2 px-2 py-1.5 rounded-md text-sm transition-colors ${
                            active
                              ? "bg-[#1b4d2e]/10 text-[#1b4d2e] font-medium"
                              : "text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900"
                          }`}
                        >
                          <span className="flex-1 truncate">{section.title}</span>
                          {group.adminOnly && (
                            <LockClosedIcon
                              className="h-3 w-3 shrink-0 text-zinc-400"
                              title="Solo visible para admins"
                            />
                          )}
                          {section.hasData && (
                            <CheckCircleIcon className="h-4 w-4 shrink-0 text-[#1b4d2e]" />
                          )}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </nav>
        </aside>

        {/* Main */}
        <main
          ref={mainRef}
          className={`flex-1 min-w-0 ${soloLectura ? "pointer-events-none opacity-75" : ""}`}
        >
          <form onSubmit={soloLectura ? (e) => e.preventDefault() : handleSubmit} className="space-y-8">
            {groups.map((group) => (
              <div key={group.id} className="space-y-4">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-zinc-400 lg:hidden">
                  {group.label}
                </h3>
                <h3 className="hidden lg:block text-xs font-semibold uppercase tracking-wider text-zinc-400 mb-3">
                  {group.label}
                </h3>
                {group.sections.map((section) => (
                  <SectionShell
                    key={section.id}
                    id={section.id}
                    title={section.title}
                    icon={section.icon}
                    hasData={section.hasData}
                  >
                    {section.render()}
                  </SectionShell>
                ))}
              </div>
            ))}
          </form>
        </main>
      </div>

      {/* Sticky save bar */}
      {!soloLectura && (
        <div
          className={`fixed bottom-0 left-0 right-0 z-30 transition-transform duration-200 ${
            isDirty || saving ? "translate-y-0" : "translate-y-full"
          }`}
          aria-hidden={!(isDirty || saving)}
        >
          <div className="mx-auto max-w-5xl px-4 pb-4">
            <div className="rounded-2xl border border-zinc-200 bg-white shadow-xl px-4 py-3 flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 text-sm text-zinc-700">
                {isDirty ? (
                  <>
                    <span
                      className="inline-block h-2 w-2 rounded-full"
                      style={{ backgroundColor: "#f59e0b" }}
                    />
                    Tienes cambios sin guardar
                  </>
                ) : (
                  <>
                    <CheckCircleIcon className="h-4 w-4 text-emerald-500" />
                    Todo guardado
                  </>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={discardChanges}
                  disabled={saving || !isDirty}
                  className="px-3 py-1.5 rounded-lg text-sm text-zinc-600 hover:bg-zinc-100 disabled:opacity-50"
                >
                  Descartar
                </button>
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={saving || !isDirty}
                  className="px-4 py-1.5 rounded-lg text-sm font-semibold text-white shadow-sm transition hover:opacity-90 disabled:opacity-50"
                  style={{ backgroundColor: ACCENT }}
                >
                  {saving ? "Guardando..." : "Guardar cambios"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VenuePage;
